import '../../styles/selfHelp.css';
import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useState } from 'react';
import useSelfHelp from '../../hooks/useSelfHelp';
import { usePhoneDialog } from './ModalSurface';
import { useHoverHelpContent } from './hoverHelpContent';

const VIEWPORT_PADDING = 8;
const TOOLTIP_GAP = 8;

function normalizeContent(content) {
    if (!content) return null;
    if (typeof content === 'string') return { body: content };
    return content;
}

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

function calculateTooltipPosition(triggerRect, tooltipRect, preferredPlacement = 'top') {
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const tooltipWidth = tooltipRect.width || 280;
    const tooltipHeight = tooltipRect.height || 80;
    const availableRight = Math.max(VIEWPORT_PADDING, viewportWidth - tooltipWidth - VIEWPORT_PADDING);
    const availableBottom = Math.max(VIEWPORT_PADDING, viewportHeight - tooltipHeight - VIEWPORT_PADDING);

    let placement = preferredPlacement;
    if (placement === 'top' && triggerRect.top - tooltipHeight - TOOLTIP_GAP < VIEWPORT_PADDING) {
        placement = 'bottom';
    } else if (placement === 'bottom' && triggerRect.bottom + tooltipHeight + TOOLTIP_GAP > viewportHeight - VIEWPORT_PADDING) {
        placement = 'top';
    }

    const centeredLeft = triggerRect.left + (triggerRect.width / 2) - (tooltipWidth / 2);
    const left = clamp(centeredLeft, VIEWPORT_PADDING, availableRight);
    const rawTop = placement === 'bottom'
        ? triggerRect.bottom + TOOLTIP_GAP
        : triggerRect.top - tooltipHeight - TOOLTIP_GAP;
    const top = clamp(rawTop, VIEWPORT_PADDING, availableBottom);

    return { top, left, placement };
}

function HoverHelp({
    helpId,
    content,
    userId,
    label = 'Show help',
    indicator = '?',
    placement = 'top',
    className = '',
}) {
    const help = useSelfHelp(userId);
    const { open, trigger: triggerRef, panel: tooltipRef, dismiss } = help;
    const onPhoneKeyDown = usePhoneDialog(tooltipRef, dismiss, open && !help.canHover, false, help.restoreFocus);
    const tooltipId = useId();
    const [position, setPosition] = useState({ top: 0, left: 0, placement });
    const rawContent = useHoverHelpContent(helpId, content);
    const helpContent = useMemo(() => normalizeContent(rawContent), [rawContent]);

    const updatePosition = useCallback(() => {
        if (!triggerRef.current || !tooltipRef.current) return;
        setPosition(calculateTooltipPosition(
            triggerRef.current.getBoundingClientRect(),
            tooltipRef.current.getBoundingClientRect(),
            placement
        ));
    }, [placement, triggerRef, tooltipRef]);

    useLayoutEffect(() => {
        if (open) updatePosition();
    }, [open, helpContent, updatePosition]);

    useEffect(() => {
        if (!open) return undefined;

        const handleViewportChange = () => updatePosition();
        window.addEventListener('resize', handleViewportChange);
        window.addEventListener('scroll', handleViewportChange, true);

        return () => {
            window.removeEventListener('resize', handleViewportChange);
            window.removeEventListener('scroll', handleViewportChange, true);
        };
    }, [open, updatePosition]);

    if (!help.enabled || !helpContent) return null;

    const describedBy = open ? tooltipId : undefined;

    return (
        <span ref={help.root} {...help.rootProps} className={`inline-flex items-center align-middle ${className}`.trim()}>
            <button
                ref={triggerRef}
                type="button"
                className="ia-self-help-trigger ml-1"
                {...help.triggerProps}
                aria-label={label}
                aria-describedby={describedBy}
                aria-expanded={open}
            >
                <span className="ia-self-help-circle">{indicator}</span>
            </button>
            {open && (
                <span
                    ref={tooltipRef}
                    id={tooltipId}
                    role={help.canHover ? "tooltip" : "dialog"}
                    aria-label={label}
                    onKeyDown={onPhoneKeyDown}
                    data-placement={position.placement}
                    className={`ia-self-help-panel ${help.canHover ? '' : 'ia-help-panel'} z-50 max-w-xs rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-left text-xs leading-5 text-white shadow-xl`}
                    style={help.canHover ? {
                        position: 'fixed',
                        top: `${position.top}px`,
                        left: `${position.left}px`,
                    } : { position: 'fixed', top: `${position.top}px`, left: `${position.left}px` }}
                >
                    {!help.canHover && <button type="button" className="ia-modal-close ia-self-help-close" aria-label="Close help" onClick={dismiss}>×</button>}
                    <span className="ia-help-body">
                    {helpContent.title && <strong className="mb-1 block text-[13px] font-semibold">{helpContent.title}</strong>}
                    <span>{helpContent.body}</span>
                    </span>
                </span>
            )}
        </span>
    );
}

export default HoverHelp;
